import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { getItemQueryKey } from "./queryKeys";
import { fetchItemInfo } from "./useItemInfo";
import { QueryKeys } from "@coworker/app/src/components/RoomSettings/hooks/queryKeys";
import { fetchItemsInfoFromDataService } from "@coworker/app/src/components/RoomSettings/services/localCombination.service";
import { Item } from "../../types/item";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";

export const useGetItemOrLocalCombination = () => {
  const storeId = useStoreId();
  const countryId = useCountryId();
  const queryClient = useQueryClient();

  return useCallback(
    async (itemNo: string) => {
      try {
        const itemInfo = await queryClient.ensureQueryData({
          queryKey: getItemQueryKey(itemNo),
          queryFn: () => fetchItemInfo(itemNo),
        });

        // If itemInfo is not found, it might be a combination article, so we check the local combination data
        const localCombinationItemInfo = itemInfo
          ? null
          : await queryClient.ensureQueryData({
              queryKey: [
                QueryKeys.CombinationArticle,
                countryId,
                storeId,
                itemNo,
              ],
              queryFn: () =>
                fetchItemsInfoFromDataService(storeId, countryId, itemNo),
            });

        const combinationItem = Array.isArray(localCombinationItemInfo)
          ? localCombinationItemInfo[0]
          : undefined;

        const itemData = itemInfo ?? (combinationItem as Item);

        return itemData;
      } catch {
        reportMessageToSentry(`Failed to fetch item`, { itemNo }, "error");
        return null;
      }
    },
    [queryClient, countryId, storeId]
  );
};
